import {
  ForwardedRef,
  Fragment,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createUseStyles } from 'react-jss';
import debounce from 'lodash/debounce';
import {
  DataGrid,
  GridPaginationModel,
  GridRowClassNameParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import {
  Dataset,
  DataTableInstance,
  DtColumn,
  DtColumnGroup,
  DtFilter,
  DtFilterValue,
  DtGridApiCommunity,
  DtPage,
  DtSort,
  getNicePageSizeOptions,
  makeDtSort,
  makeMuiColumnGroupingModel,
  makeMuiDataGridColumns,
  makeMuiRows,
  makeMuiSort,
  PaginationMode,
  PaginationProps,
} from './helpers';
import Pagination from './pagination';
import { alpha, Box, Stack, Theme } from '@mui/material';
import { QueryKey, useQuery } from 'react-query';
import Text from '../text';
import usePrior from '../../hooks/use-prior.hook';
import isEqual from 'lodash/isEqual';
import FilterHeader from './filter-header';
import Panel from '../panel';
import PanelContent from '../panel/panel-content';
import { cloneDeep } from 'lodash';
import useQueryHelper from '@/hooks/use-query-helper';
import useDomSize from '@/hooks/use-dom-size.hook';
import { LoadState } from '@/lib/helpers';
import { SavedFilterItem, isSavedFilterMatch } from '@/lib/saved-filters';
import { useSearch } from '@tanstack/react-router';
import NoResultsOverlay from './no-results-overlay';
import LoadingOverlay from './loading-overlay';
import NoRowsOverlay from './no-rows-overlay';

interface Props {
  title?: string;
  columns: DtColumn[];
  queryKey: QueryKey;
  rowHeight?: number;
  search?: boolean;
  searchPlaceholder?: string;
  selectedRowId?: number | string;
  onLoad: (
    pageParams: DtPage,
    filter?: DtFilter,
    polling?: boolean
  ) => Promise<Dataset<Record<string, ReactNode>>>;
  onLoadCount?: (filters?: DtFilter) => Promise<{ count: string }>;
  onRowClick?: (rowId: string) => void;
  onChangeFilter?: (filterValues: DtFilterValue[]) => DtFilterValue[];
  onExport?: (page: number, pageSize: number, sort?: DtSort, filters?: DtFilter) => Promise<void>;
  ref?: ForwardedRef<HTMLDivElement>;
  filter?: DtFilter;
  sort?: DtSort;
  poll?: number;
  mode?: PaginationMode;
  countSuffix?: string;
  savedFilters?: SavedFilterItem[];
  columnGroups?: DtColumnGroup[];
  exportTooltip?: string;
}

const useStyles = createUseStyles<string, { canRowClick: boolean; rowsHeight: number }>(
  (theme: Theme) => ({
    dataTable: {
      boxSizing: 'border-box',
      '& .MuiDataGrid-root': {
        border: 'none',
      },
      '& .MuiDataGrid-row': {
        cursor: ({ canRowClick }) => (canRowClick ? 'pointer' : 'default'),
        '&.selected': {
          backgroundColor: theme.palette.subtleGray,
        },
      },
      '& .MuiDataGrid-virtualScroller': {
        height: ({ rowsHeight }) => rowsHeight,
      },
      '& .MuiDataGrid-columnHeaders': {
        border: 'none',
      },
      '& .MuiDataGrid-columnHeader': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&.MuiDataGrid-columnHeaderTitle': {
          fontWeight: theme.palette.darkGray,
        },
        '&.MuiDataGrid-withBorderColor': {
          borderColor: theme.palette.divider,
          outline: 'none',
        },
      },
      '& .MuiDataGrid-cell': {
        borderColor: theme.palette.divider,
        whiteSpace: 'normal !important',
        '& .MuiDataGrid-cellContent': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        },
        '& > *': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        },
      },
      '& .MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
      '& .MuiTablePagination-displayedRows': {
        display: 'none',
      },
      '& .MuiDataGrid-footerContainer': {
        paddingLeft: 10,
      },
      pagination: {
        display: 'flex',
      },
      '& .dt-column-group': {
        backgroundColor: '#f0f4f8',
        color: '#1a365d',
        fontWeight: 'bold',
        borderBottom: 'none',
        textAlign: 'center',
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          border: 'none',
        },
      },
      '& .MuiDataGrid-columnHeader.dt-column-group-column': {
        backgroundColor: '#fafbfe',
      },
      '& .MuiDataGrid-cell.dt-column-group-column': {
        backgroundColor: '#fafbfe',
      },
      '& .MuiDataGrid-row:hover .MuiDataGrid-cell.dt-column-group-column': {
        backgroundColor: alpha('#fafbfe', 0.7),
      },
      '& .MuiDataGrid-row.Mui-hovered .MuiDataGrid-cell.dt-column-group-column': {
        backgroundColor: alpha('#fafbfe', 0.7),
      },
    },
    cell: {
      outline: 'none !important',
    },
    row: {},
    panelContent: {
      paddingTop: 0,
      paddingBottom: 0,
    },
  })
);

const PageSizeOptions = [10, 50, 100] as const;

const DataTable = forwardRef<DataTableInstance, Props>(
  (
    {
      title = '',
      columns,
      filter: propFilter,
      onLoad,
      onLoadCount,
      onRowClick,
      onChangeFilter,
      onExport,
      queryKey,
      rowHeight = 50,
      search = false,
      searchPlaceholder = '',
      selectedRowId,
      poll = null,
      sort: propSort,
      mode = 'page',
      countSuffix = '',
      savedFilters,
      columnGroups,
      exportTooltip,
    },
    ref
  ) => {
    const { getSize, height: availableHeight } = useDomSize();
    const [pageParams, setPageParams] = useState<DtPage>({
      page: 0,
      pageSize: PageSizeOptions[0],
      sort: propSort,
      enablePolling: !!poll,
    });

    const [pageSizeOptions, setPageSizeOptions] = useState<number[]>([...PageSizeOptions]);
    const [measured, setMeasured] = useState(false);
    const [exportLoadState, setExportLoadState] = useState<LoadState>('unloaded');

    const urlSearch = useSearch({ strict: false });
    const urlKeyword = (urlSearch as Record<string, string | undefined>)?.keyword;
    const initialKeyword = urlKeyword ?? propFilter?.keyword ?? '';

    const [filter, setFilter] = useState<DtFilter>(() => {
      const base = propFilter || { keyword: '', fields: [], values: [] };
      return { ...base, keyword: initialKeyword };
    });
    const [keywordVal, setKeywordVal] = useState(initialKeyword);

    const activeSavedFilterId = useMemo(() => {
      if (!savedFilters?.length) {
        return '';
      }
      const match = savedFilters.find((savedFilter) =>
        isSavedFilterMatch(savedFilter, { values: filter.values, keyword: keywordVal })
      );
      return match?.id || '';
    }, [savedFilters, filter.values, keywordVal]);

    const apiRef = useGridApiRef();

    const applyFilterValues = useCallback(
      (nextValues: DtFilterValue[], nextKeyword = '') => {
        let updatedValues = cloneDeep(nextValues);
        if (onChangeFilter) {
          updatedValues = onChangeFilter(updatedValues);
        }

        setFilter((old) => ({
          ...old,
          values: updatedValues,
          keyword: nextKeyword,
        }));
        setKeywordVal(nextKeyword);
        setPageParams((old) => ({
          ...old,
          page: 0,
        }));
      },
      [onChangeFilter]
    );

    const isCursorMode = mode === 'cursor';
    const hasSeparateCount = !!onLoadCount;

    const refetchInterval = useMemo(() => {
      const { page, enablePolling } = pageParams;

      if (!poll || page !== 0 || !enablePolling) {
        return false;
      }

      return poll;
    }, [poll, pageParams]);

    const query = useQuery(
      [queryKey],
      () => {
        return onLoad(pageParams, filter, !!refetchInterval);
      },
      { enabled: measured, refetchInterval, refetchIntervalInBackground: false }
    );
    const { data, refetch } = query;
    const { showLoader, isFetching } = useQueryHelper(query);

    const { rows = [], total = 0 } = data || {};

    const countQuery = useQuery([queryKey, 'count'], () => onLoadCount?.(filter), {
      enabled: hasSeparateCount,
    });
    const { data: countData, refetch: countRefetch } = countQuery;
    const { count } = countData || {};

    const { showLoader: showCountLoader } = useQueryHelper(countQuery);
    const countLabel = hasSeparateCount ? `${count} ${countSuffix}` : '';

    const styles = useStyles({
      canRowClick: !!onRowClick,
      rowsHeight: rowHeight * Math.max(rows.length, 1),
    });

    const muiColumns = useMemo(
      () => makeMuiDataGridColumns(columns, columnGroups),
      [columns, columnGroups]
    );
    const muiRows = useMemo(() => makeMuiRows(rows, columns), [rows, columns]);

    // disabled queries are still run when calling refetch, so we must manually check the usage condition
    const safeCountRefetch = useCallback(
      () => hasSeparateCount && countRefetch(),
      [hasSeparateCount, countRefetch]
    );

    // make refetch available to child components
    (apiRef.current as DtGridApiCommunity).refresh = () => {
      refetch();
      safeCountRefetch();
    };

    // make refetch available to parent components
    useImperativeHandle(ref, () => {
      return {
        refresh: () => {
          (apiRef.current as DtGridApiCommunity).refresh();
        },
      };
    }, [apiRef]);

    const handleExport = () => {
      const { page, pageSize, sort } = pageParams;
      setExportLoadState('loading');
      onExport?.(page, pageSize, sort, filter).then(() => {
        setExportLoadState('loaded');
      });
    };

    const handlePagination = useCallback((model: GridPaginationModel) => {
      const { page, pageSize } = model;

      setPageParams((old) => {
        const pageChanged = page !== old.page;
        return {
          ...old,
          page,
          pageSize,
          enablePolling: pageChanged ? false : old.enablePolling,
        };
      });
    }, []);

    const handleSort = (model: GridSortModel) => {
      let updatedSort: DtSort | null = null;

      if (!model?.length) {
        // when you change the sort direction on the same column, it cycles betwee 'asc' | 'desc' | null
        // so the third time you click the button, model = [] ???
        const { sort } = pageParams;
        if (sort?.columnName) {
          updatedSort = { columnName: sort?.columnName, direction: 'asc' };
        }
      } else {
        updatedSort = makeDtSort(model);
      }

      if (!updatedSort) {
        return;
      }

      setPageParams((old) => ({
        ...old,
        page: 0,
        sort: updatedSort!,
        enablePolling: false,
      }));
    };

    const handleChangeKeyword = useCallback((keyword: string) => {
      const safeKeyword = String(keyword).trim();

      if (safeKeyword.length && safeKeyword.length < 3) {
        return;
      }

      setFilter((old) => ({
        ...old,
        keyword: safeKeyword,
      }));

      setPageParams((old) => ({
        ...old,
        page: 0,
      }));
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceHandleChangeKeyword = useCallback(
      debounce((keyword: string) => {
        handleChangeKeyword(keyword);
      }, 400),
      [handleChangeKeyword]
    );

    const handleFilterChange = useCallback(
      (newValues: DtFilterValue[]) => {
        let updatedValues = newValues;
        if (onChangeFilter) {
          updatedValues = onChangeFilter(updatedValues);
        }
        setFilter((old) => ({
          ...old,
          values: updatedValues,
        }));
        setPageParams((old) => ({
          ...old,
          page: 0,
        }));
      },
      [onChangeFilter]
    );

    const handleApplySavedFilter = useCallback(
      (savedFilterId: string) => {
        const savedFilter = savedFilters?.find(({ id }) => id === savedFilterId);
        if (savedFilter) {
          applyFilterValues(savedFilter.values, '');
        }
      },
      [applyFilterValues, savedFilters]
    );

    const handlePollingChange = () => {
      setPageParams((old) => {
        const updated = !old.enablePolling;

        return {
          ...old,
          enablePolling: updated,
          page: updated ? 0 : old.page,
        };
      });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleGetRowClassName = ({ id }: GridRowClassNameParams<any>) => {
      if (selectedRowId === id) {
        return 'selected';
      }
      return '';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRowClick = (params: GridRowClassNameParams<any>) => {
      onRowClick?.(params.id as string);
    };

    // pagination params changed? reload
    const priorPageParams = usePrior(pageParams);
    useEffect(() => {
      if (priorPageParams && !isEqual(pageParams, priorPageParams)) {
        refetch();
      }
    }, [pageParams, priorPageParams, refetch]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceRefetch = useCallback(
      debounce(() => {
        refetch();
        safeCountRefetch();
      }, 1500),
      [refetch, safeCountRefetch]
    );

    useEffect(() => {
      debounceHandleChangeKeyword(keywordVal);
    }, [keywordVal, debounceHandleChangeKeyword]);

    useEffect(() => {
      setKeywordVal(urlKeyword || '');
    }, [urlKeyword]);

    // filters changed? reload
    const priorFilter = usePrior(filter);
    useEffect(() => {
      if (priorFilter && !isEqual(filter, priorFilter)) {
        debounceRefetch();
      }
    }, [filter, priorFilter, debounceRefetch]);

    // once on page load, determine the nice page size, make sure it's included in pageSizeOptions, and mark the table as "measured" by flipping measured to true
    useEffect(() => {
      if (measured) {
        return;
      }

      const { height } = getSize();
      const nicePageSizeOptions = getNicePageSizeOptions(rowHeight, PageSizeOptions, height);

      setPageParams((old) => ({
        ...old,
        pageSize: nicePageSizeOptions[0],
      }));

      setPageSizeOptions(nicePageSizeOptions);

      setTimeout(() => {
        setMeasured(true);
      }, 0);
    }, [measured, getSize, rowHeight]);

    // window resized? update the nice page size option
    useEffect(() => {
      if (!measured || availableHeight <= 0) {
        return;
      }

      const nextPageSizeOptions = getNicePageSizeOptions(
        rowHeight,
        PageSizeOptions,
        availableHeight
      );
      const nextPageSize = nextPageSizeOptions[0];

      setPageParams((old) => {
        if (old.pageSize === nextPageSize) {
          return old;
        }

        return {
          ...old,
          pageSize: nextPageSize,
        };
      });

      setPageSizeOptions((old) => (isEqual(old, nextPageSizeOptions) ? old : nextPageSizeOptions));
    }, [measured, availableHeight, rowHeight]);

    const hasTitle = !!title;
    const hasData = !!data;
    const hasExport = !!onExport;
    const isLoadingExport = exportLoadState === 'loading';
    const hasColumnGroups = !!columnGroups?.length;

    const { page, sort, enablePolling } = pageParams;

    return (
      <Box ref={ref} pb={2}>
        <Panel>
          <Stack width="100%" direction="column" minHeight={40} px={2.5} pt={1}>
            {hasTitle && (
              <Box width="50%">
                <Text size="large" color="active">
                  {title}
                </Text>
              </Box>
            )}
            <FilterHeader
              filter={filter}
              values={filter.values}
              onFilterChange={handleFilterChange}
              keyword={keywordVal}
              onKeywordChange={search ? setKeywordVal : undefined}
              searchPlaceholder={searchPlaceholder}
              savedFilters={savedFilters}
              activeSavedFilterId={activeSavedFilterId}
              onApplySavedFilter={handleApplySavedFilter}
            />
          </Stack>
          <PanelContent className={styles.panelContent}>
            <Box className={styles.dataTable}>
              <DataGrid
                apiRef={apiRef}
                rows={muiRows}
                columns={muiColumns}
                getRowClassName={handleGetRowClassName}
                rowHeight={rowHeight}
                onSortModelChange={handleSort}
                onPaginationModelChange={handlePagination}
                onRowClick={handleRowClick}
                pageSizeOptions={pageSizeOptions}
                pagination
                slots={{
                  pagination: Pagination,
                  noRowsOverlay: hasData ? NoRowsOverlay : Fragment,
                  noResultsOverlay: NoResultsOverlay,
                  loadingOverlay: LoadingOverlay,
                }}
                slotProps={{
                  pagination: {
                    page,
                    count: hasSeparateCount ? count : total,
                    onExport: hasExport ? handleExport : undefined,
                    exportTooltip,
                    isLoadingExport,
                    isLoading: isFetching,
                    isCursorMode,
                    enablePolling,
                    onPollingChange: handlePollingChange,
                    countLabel,
                    countLoading: showCountLoader,
                  } as Omit<PaginationProps, 'onPageChange'>,
                  row: { className: styles.row },
                  cell: { className: styles.cell },
                }}
                paginationMode="server"
                sortingMode="server"
                paginationModel={pageParams}
                sortModel={sort ? [makeMuiSort(sort)] : undefined}
                rowCount={total}
                checkboxSelection={false}
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                loading={showLoader}
                columnGroupingModel={
                  hasColumnGroups ? makeMuiColumnGroupingModel(columnGroups) : undefined
                }
                experimentalFeatures={{ columnGrouping: hasColumnGroups }}
              />
            </Box>
          </PanelContent>
        </Panel>
      </Box>
    );
  }
);

DataTable.displayName = 'DataTable';

export default DataTable;
